import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Button, TextField, Alert, LinearProgress, Typography } from '@mui/material';
import useFetch from '../../hooks/useFetch';
import PhotoCapture from './PhotoCapture';
import CheckEditor from './CheckEditor';
import VendorPicker from './VendorPicker';
import usePhotoReading from './usePhotoReading';
import { draftFromInvoiceReading, checksFromReading, reviewSummary, checksComplete } from './invoiceDraft';
import { parseMoney, formatCents } from './money';
import { formatDay } from './dates';

const empty = { vendorId: undefined, invoiceNumber: '', invoiceDate: '', totalText: '', lowConfidence: new Set() };
const duplicateCheckNumber = error => /^check number (\d+) is already on the calendar/.exec(error || '');

export default function InvoiceFlow({ date, accounts, setAuth, onSaved, onCancel, resume }) {
  const { t, i18n } = useTranslation();
  const { fetchResource, isPending } = useFetch({ setAuth });
  const [step, setStep] = useState(resume ? 'invoice' : 'start');
  const [vendors, setVendors] = useState([]);
  const [vendorsLoaded, setVendorsLoaded] = useState(false);
  const [draft, setDraft] = useState(empty);
  const [checks, setChecks] = useState([]);
  const [readingIds, setReadingIds] = useState(resume?.readingIds || []);
  const [checkReadingIds, setCheckReadingIds] = useState([]);
  const [photoKey, setPhotoKey] = useState(null);
  const [error, setError] = useState('');
  const [returned, setReturned] = useState({ invoice: null, checks: [] });
  const [canGenerateChecks, setCanGenerateChecks] = useState(false);
  const generateRef = useRef(null);
  const invoiceReader = usePhotoReading({ fetchResource });
  const checksReader = usePhotoReading({ fetchResource });

  const loadVendors = loadingKey => fetchResource({ method: 'GET', resource: 'calendar/vendors', loadingKey }).then(r => {
    if (!r.error) { setVendors(r.vendors || []); setVendorsLoaded(true); }
    return r;
  });

  useEffect(() => {
    loadVendors().then(r => { if (r.error) setError(r.error); });
  }, [fetchResource]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (resume?.reading?.result && vendorsLoaded) {
      setDraft(draftFromInvoiceReading(resume.reading.result, vendors));
      setPhotoKey(resume.reading.photo_key);
      setReturned(x => ({ ...x, invoice: resume.reading.result }));
    }
  }, [vendorsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (invoiceReader.state === 'done') {
      const r = invoiceReader.readings.find(x => x.status === 'done');
      setDraft(draftFromInvoiceReading(r.result, vendors));
      setPhotoKey(r.photo_key);
      setReturned(x => ({ ...x, invoice: r.result }));
      setStep('invoice');
    }
    if (invoiceReader.state === 'failed') { setError(t('calendar.invoice.readFailed')); setStep('invoice'); }
  }, [invoiceReader.state]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (checksReader.state === 'done') {
      const fresh = checksFromReading(checksReader.mergedChecks, accounts);
      setChecks(existing => {
        const byNumber = new Map(existing.map(c => [c.checkNumber, c]));
        fresh.forEach(c => { if (!byNumber.has(c.checkNumber)) byNumber.set(c.checkNumber, c); });
        return [...byNumber.values()];
      });
      setReturned(x => ({ ...x, checks: checksReader.mergedChecks }));
    }
    if (checksReader.state === 'failed') setError(t('calendar.invoice.readFailed'));
  }, [checksReader.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files, kind) => {
    const form = new FormData();
    files.forEach(f => form.append('photos', f));
    form.append('kind', kind);
    if (kind === 'checks' && draft.vendorId) form.append('vendorId', draft.vendorId);
    const r = await fetchResource({ resource: 'calendar/photos', body: form, loadingKey: 'photo' });
    if (r.error) return setError(r.error);
    setError('');
    setReadingIds(ids => [...ids, ...r.readingIds]);
    if (kind === 'invoice') {
      invoiceReader.start(r.readingIds);
      setStep('reading');
    } else {
      const next = [...checkReadingIds, ...r.readingIds];
      setCheckReadingIds(next);
      checksReader.start(next);
    }
  };

  const totalCents = parseMoney(draft.totalText);
  const invoiceReady = draft.vendorId && /^\d{4}-\d{2}-\d{2}$/.test(draft.invoiceDate) && totalCents;
  const summary = reviewSummary(draft, checks, accounts, vendors);

  const goReview = () => {
    const list = checks.length ? checks : (generateRef.current?.() || []);
    if (checksComplete(list)) setStep('review');
  };

  const save = async () => {
    setError('');
    const vendorName = vendors.find(v => v.id === draft.vendorId)?.name || '';
    const body = {
      vendorId: draft.vendorId,
      invoiceNumber: draft.invoiceNumber || null, invoiceDate: draft.invoiceDate, totalCents, photoKey,
      bankAccountId: checks[0]?.bankAccountId ?? accounts.find(a => a.role === 'operating')?.id,
      checks: checks.map(c => ({ checkNumber: c.checkNumber, dueDate: c.dueDate, amountCents: parseMoney(c.amountText), bankAccountId: c.bankAccountId })),
      readingIds,
      confirmed: readingIds.length ? {
        invoice: { vendor_name: vendorName, invoice_number: draft.invoiceNumber || null, invoice_date: draft.invoiceDate, total_cents: totalCents },
        checks: checks.map(c => ({ check_number: c.checkNumber, amount_cents: parseMoney(c.amountText), date: c.dueDate,
          account_last4: accounts.find(a => a.id === c.bankAccountId)?.account_last4 ?? null })),
      } : null,
    };
    const r = await fetchResource({ resource: 'calendar/invoices', body, loadingKey: 'save' });
    if (r.error) {
      const dup = duplicateCheckNumber(r.error);
      return setError(dup ? t('calendar.checks.duplicate', { number: dup[1] }) : r.error);
    }
    onSaved();
  };

  if (step === 'start') return (
    <Stack spacing={2}>
      {error && <Alert severity='warning'>{error}</Alert>}
      <PhotoCapture onFiles={files => upload(files, 'invoice')} />
      <Button variant='outlined' size='large' onClick={() => setStep('invoice')}>{t('calendar.invoice.typeIt')}</Button>
    </Stack>
  );

  if (step === 'reading') return (
    <Stack spacing={2}>
      <Typography>{t('calendar.invoice.reading')}</Typography>
      <LinearProgress />
      <Button onClick={onCancel}>{t('calendar.invoice.readLater')}</Button>
    </Stack>
  );

  if (step === 'invoice') return (
    <Stack spacing={2}>
      {error && <Alert severity='warning'>{error}</Alert>}
      <VendorPicker vendors={vendors} loadVendors={loadVendors} fetchResource={fetchResource} isPending={isPending}
        value={draft.vendorId} onChange={id => setDraft(d => ({ ...d, vendorId: id }))} error={draft.lowConfidence.has('vendor_name')} />
      <TextField label={t('calendar.invoice.number')} value={draft.invoiceNumber} error={draft.lowConfidence.has('invoice_number')} onChange={e => setDraft(d => ({ ...d, invoiceNumber: e.target.value }))} />
      <TextField label={t('calendar.invoice.date')} type='date' value={draft.invoiceDate} error={draft.lowConfidence.has('invoice_date')} InputLabelProps={{ shrink: true }} onChange={e => setDraft(d => ({ ...d, invoiceDate: e.target.value }))} />
      <TextField label={t('calendar.invoice.total')} value={draft.totalText} inputProps={{ inputMode: 'decimal' }} error={draft.lowConfidence.has('total_cents') || (Boolean(draft.totalText) && !totalCents)} onChange={e => setDraft(d => ({ ...d, totalText: e.target.value }))} />
      {draft.lowConfidence.size > 0 && <Alert severity='info'>{t('calendar.invoice.checkLowConfidence')}</Alert>}
      <Stack direction='row' spacing={1}>
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
        <Button variant='contained' disabled={!invoiceReady} onClick={() => setStep('checks')}>{t('calendar.checks.generator')}</Button>
      </Stack>
    </Stack>
  );

  if (step === 'checks') return (
    <Stack spacing={2}>
      {error && <Alert severity='warning'>{error}</Alert>}
      <CheckEditor date={date} totalCents={totalCents} accounts={accounts} checks={checks} setChecks={setChecks} generateRef={generateRef}
        onCanGenerateChange={setCanGenerateChecks}
        onPhotos={<PhotoCapture label={t('calendar.checks.photos')} multiple onFiles={files => upload(files, 'checks')} />} />
      {checksReader.state === 'reading' && <LinearProgress />}
      <Stack direction='row' spacing={1}>
        <Button onClick={() => setStep('invoice')}>{t('common.back')}</Button>
        <Button variant='contained' disabled={checks.length > 0 ? !checksComplete(checks) : !canGenerateChecks} onClick={goReview}>{t('calendar.review.next')}</Button>
      </Stack>
    </Stack>
  );

  return (
    <Stack spacing={2}>
      {error && <Alert severity='error'>{error}</Alert>}
      <Typography>
        {summary.allSame
          ? t('calendar.review.sentence', { count: summary.count, vendor: summary.vendorName, amount: formatCents(summary.amountCents), account: summary.accountName, date: formatDay(summary.firstDate, i18n.language), total: summary.sumText })
          : `${summary.count} × ${summary.vendorName} — ${summary.sumText}`}
      </Typography>
      <Alert severity={summary.diffCents === 0 ? 'success' : 'warning'}>
        {summary.diffCents === 0 ? t('calendar.review.matches') : t('calendar.review.mismatch', { diff: formatCents(Math.abs(summary.diffCents)) })}
      </Alert>
      <Stack direction='row' spacing={1}>
        <Button onClick={() => setStep('checks')}>{t('common.back')}</Button>
        <Button variant='contained' disabled={isPending('save')} onClick={save}>{t('calendar.review.save')}</Button>
      </Stack>
    </Stack>
  );
}
